/**
 * HealthPanel — MOPH "3 หมอ" primary-care coverage across Sikhio DISTRICT.
 *
 * Scope warning, deliberately loud in the UI: this is อำเภอสีคิ้ว — all 12
 * tambons, 96,969 people — not เทศบาลเมืองสีคิ้ว (17,975). A mayor reading
 * "96,969" on their municipal dashboard would reasonably take it for their
 * own population, so the header states the unit and the municipality's own
 * figure sits beside it for contrast.
 *
 * หมอ 1 = อสม. village health volunteer · หมอ 2 = รพ.สต. staff ·
 * หมอ 3 = family-doctor team.
 */

import type { FallbackTier, HealthTambon } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  data: HealthTambon[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
  /** Municipal population, for the district-vs-municipality contrast line. */
  municipalPopulation?: number | null;
}

/** Tambon rows shown before the list is truncated — keeps the rail scannable. */
const VISIBLE_ROWS = 6;

const fmtInt = (n: number) => n.toLocaleString("en-US");

export function HealthPanel({
  data,
  loading,
  ageMinutes,
  fallbackTier,
  municipalPopulation,
}: Props) {
  if (loading && data.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">สาธารณสุข / 3 หมอ</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "70%" }} />
      </div>
    );
  }
  if (!data.length) return null;

  const population = data.reduce((s, t) => s + t.population, 0);
  const vhv = data.reduce((s, t) => s + t.vhvCount, 0);
  const teams = data.reduce((s, t) => s + t.familyDoctorTeamCount, 0);
  const bedridden = data.reduce((s, t) => s + t.elderlyBedridden, 0);
  const homebound = data.reduce((s, t) => s + t.elderlyHomebound, 0);
  const perVhv = vhv > 0 ? Math.round(population / vhv) : null;

  // Ranked by dependency load — the tambons where outreach matters most.
  const byDependency = [...data]
    .sort((a, b) => b.elderlyBedridden + b.elderlyHomebound - (a.elderlyBedridden + a.elderlyHomebound))
    .slice(0, VISIBLE_ROWS);

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="สาธารณสุข · 3 หมอ อำเภอสีคิ้ว"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="moph.3doctor"
      />

      <div className="caption" style={{ color: "var(--text-3)" }}>
        ทั้งอำเภอ {data.length} ตำบล · {fmtInt(population)} คน
        {municipalPopulation ? ` — เขตเทศบาลเอง ${fmtInt(municipalPopulation)} คน` : ""}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", marginTop: 4 }}>
        <span className="caption">อสม. (หมอประจำบ้าน)</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{fmtInt(vhv)} คน</span>

        <span className="caption">ประชากรต่อ อสม. 1 คน</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{perVhv != null ? `${perVhv} คน` : "—"}</span>

        <span className="caption">ทีมหมอครอบครัว</span>
        <span className="mono caption" style={{ textAlign: "right" }}>{fmtInt(teams)} ทีม</span>

        <span className="caption">ผู้สูงอายุติดบ้าน / ติดเตียง</span>
        <span
          className="mono caption"
          style={{ textAlign: "right", color: bedridden > 0 ? "var(--warn)" : undefined }}
        >
          {fmtInt(homebound)} / {fmtInt(bedridden)}
        </span>
      </div>

      <div className="caption" style={{ marginTop: 6, color: "var(--text-3)" }}>
        ตำบลที่มีภาระดูแลสูงสุด
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "2px 12px" }}>
        {byDependency.map((t) => (
          <div key={t.tamid} style={{ display: "contents" }}>
            <span style={{ fontSize: "var(--size-eyebrow)" }}>{t.name}</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              ติดเตียง {t.elderlyBedridden} · อสม. {t.vhvCount}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
